import React, { useEffect, useState } from "react";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { useLanguage } from "../Translations/LenguageContext";
import { useNavigate, useParams } from "react-router-dom";
import GeoEngines from "./GeoSetup/GeoEngines";
import GeoContext from "./GeoSetup/GeoContext";
import SettingsGeoModal from "./SettingsGeoModal";
import DeleteItemModal from "../Components/DeleteItemModal";
import GeoGoal from "./GeoSetup/GeoGoal";
import GeoOverview from "./GeoOverview/GeoOverview";
import GeoPrompts from "./GeoSetup/GeoPrompts/GeoPrompts";
import GeoTopics from "./GeoSetup/GeoTopics/GeoTopics";
import GeoLanguage from "./GeoSetup/GeoLanguage";
import MainGeoLoader from "../Loaders/MainGeoLoader";
import GeoProperty from "./GeoSetup/GeoProperty";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const OpenGeo = ({
    account_id,
    userData,
    singleOrgData,
    loadingSingleOrg,
    fetchSingleOrganization
}) => {
    const { t } = useLanguage()
    const navigate = useNavigate()
    const { org_id, geo_id, geo_page } = useParams()
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([])
    const [openSettingsModal, setOpenSettingsModal] = useState(false)
    const [openDeleteGeo, setOpenDeleteGeo] = useState(false)
    const [selectedContexts, setSelectedContexts] = useState([]);
    const [goalDesc, setGoalDesc] = useState("")

    const [loadingSave, setLoadingSave] = useState(false)
    const [errorMessage, setErrorMessage] = useState({ message: "", display: false });
    const [selectedTopics, setSelectedTopics] = useState([])
    const [selectedEngines, setSelectedEngines] = useState([]);
    const [count, setCount] = useState(20);
    const [promptsGenStatus, setPromptsGenStatus] = useState(null)
    const [selectedLanguage, setSelectedLanguage] = useState("")
    const [promptSumulationsRun, setPromptSumulationsRun] = useState(false)
    const [selectedPropertyId, setSelectedPropertyId] = useState("")
    const [geoSettings, setGeoSettings] = useState({})

    const [geoData, setGeoData] = useState({
        id: "",
        name: "",
        description: "",
        property_id: "",
        contexts: "",
        contexts_selected: "",
        goal: "",
        topics_gen_status: "",
        topics: "",
        topics_selected: "",
        engines: "",
        simulation_count: "",
        simulation_completed: "",
        created_at: "",
        language: "",
        prompts_run_status: "",
    });

    const fetchData = async () => {
        setLoading(true);

        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/get_geo_optimization`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        org_id,
                        geo_id
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            setData(
                result.data
                    ? {
                        ...result.data,
                        sources: [...(result.data.sources || [])].sort(
                            (a, b) =>
                                new Date(b.created_at).getTime() -
                                new Date(a.created_at).getTime()
                        ),
                    }
                    : null
            );

            setGeoData({
                id: result.data.id,
                name: result.data.name,
                description: result.data.description,
                property_id: result.data.property_id,
                contexts: result.data.contexts,
                contexts_selected: result.data.contexts_selected,
                goal: result.data.goal,
                topics_gen_status: result.data.topics_gen_status,
                topics: result.data.topics,
                topics_selected: result.data.topics_selected,
                engines: result.data.engines,
                simulation_count: result.data.simulation_count,
                simulation_completed: result.data.simulation_completed,
                created_at: result.data.created_at,
                language: result.data.language,
                prompts_run_status: result.data.prompts_run_status
            })


            if (result.data.property_id) {
                setSelectedPropertyId(result.data.property_id)
            }

            if (result.data.language) {
                setSelectedLanguage(result.data.language)
            }

            if (result.data.contexts_selected) {
                setSelectedContexts(result.data.contexts)
            }

            if (result.data.goal) {
                setGoalDesc(result.data.goal)
            }

            if (!result.data.property_id) {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/property`)
            } else if (!result.data.language) {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/language`)
            } else if (!result.data.contexts_selected) {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/context`)
            } else if (!result.data.goal) {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/goal`)
            } else if (result.data.topics_gen_status == "PENDING") {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/goal`)
            } else if (!result.data.topics_selected) {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/topics`)
            } else if (!result.data.engines || !result.data.simulation_count) {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/engines`)
            } else if (result.data.prompts_gen_status != "COMPLETED") {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/engines`)
            } else if (!result.data.simulation_completed) {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/simulation-prompts`)
            }

            if (result.data.simulation_completed) {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/geo-overview/overview`)
            }

        } catch (err) {
            console.error("Error fetching GEO optimization:", err);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    };

    const fetchGeoSettings = async () => {

        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/get_geo_settings`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        org_id,
                        geo_id
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            setGeoSettings(result.data)

        } catch (err) {
            console.error("Error fetching GEO settings:", err);
        }
    };

    const savePropertyGeo = async () => {
        setSelectedContexts([])
        setGoalDesc("")
        setSelectedLanguage("")
        if (!selectedPropertyId.trim()) {
            setErrorMessage({ message: "Property is required", display: true });
            return;
        }

        setLoadingSave(true);
        setErrorMessage({ message: "", display: false });

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/geo_setup`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id: account_id,
                    org_id: org_id,
                    geo_id: geo_id,
                    property_id: selectedPropertyId,
                    language: null,
                    contexts: null,
                    goal: null,
                    topics: null,
                    engines: null,
                    simulation_count: null
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Something went wrong");
            }

            if (data.message == "OK") {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/language`);
            }

        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true
            });
        } finally {
            setLoadingSave(false);
        }
    };

    const saveLanguageGeo = async () => {
        setSelectedContexts([])
        setGoalDesc("")
        if (!selectedLanguage.trim()) {
            setErrorMessage({ message: "Language is required", display: true });
            return;
        }

        setLoadingSave(true);
        setErrorMessage({ message: "", display: false });

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/geo_setup`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id: account_id,
                    org_id: org_id,
                    geo_id: geo_id,
                    property_id: null,
                    language: selectedLanguage,
                    contexts: null,
                    goal: null,
                    topics: null,
                    engines: null,
                    simulation_count: null
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Something went wrong");
            }

            if (data.message == "OK") {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/context`);
            }

        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true
            });
        } finally {
            setLoadingSave(false);
        }
    };

    const saveContextGeo = async () => {
        if (selectedContexts.length == 0) {
            setErrorMessage({ message: "At least one context is required", display: true });
            return;
        }

        setLoadingSave(true);
        setErrorMessage({ message: "", display: false });

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/geo_setup`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id: account_id,
                    org_id: org_id,
                    geo_id: geo_id,
                    property_id: null,
                    language: null,
                    contexts: selectedContexts,
                    goal: null,
                    topics: null,
                    engines: null,
                    simulation_count: null
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Something went wrong");
            }

            if (data.message == "OK") {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/goal`);
            }

        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true
            });
        } finally {
            setLoadingSave(false);
        }
    };

    const saveGoalGeo = async () => {
        if (!goalDesc) {
            setErrorMessage({ message: "Goal is required...", display: true });
            return;
        }

        if (goalDesc.length > 500) {
            setErrorMessage({ message: "Max 500 characters allowed...", display: true });
            return;
        }

        setLoadingSave(true);
        setErrorMessage({ message: "", display: false });

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/geo_setup`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id: account_id,
                    org_id: org_id,
                    geo_id: geo_id,
                    property_id: null,
                    language: null,
                    contexts: null,
                    goal: goalDesc,
                    topics: null,
                    engines: null,
                    simulation_count: null
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Something went wrong");
            }

            if (data.message == "OK") {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/topics`);
            }

        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true
            });
        } finally {
            setLoadingSave(false);
        }
    };

    const saveTopicsGeo = async () => {
        if (selectedTopics.length < 5) {
            setErrorMessage({ message: "At least 5 topics are required...", display: true });
            return;
        }

        setLoadingSave(true);
        setErrorMessage({ message: "", display: false });

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/geo_setup`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id: account_id,
                    org_id: org_id,
                    geo_id: geo_id,
                    property_id: null,
                    language: null,
                    contexts: null,
                    goal: null,
                    topics: selectedTopics,
                    engines: null,
                    simulation_count: null
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Something went wrong");
            }

            if (data.message == "OK") {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/engines`);
            }

        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true
            });
        } finally {
            setLoadingSave(false);
        }
    };

    const saveEnginesGeo = async () => {
        if (selectedEngines.length == 0) {
            setErrorMessage({ message: "At least 1 engine is required...", display: true });
            return;
        }

        setLoadingSave(true);
        setErrorMessage({ message: "", display: false });

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/geo_setup`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id: account_id,
                    org_id: org_id,
                    geo_id: geo_id,
                    property_id: null,
                    language: null,
                    contexts: null,
                    goal: null,
                    topics: null,
                    engines: selectedEngines,
                    simulation_count: count
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Something went wrong");
            }

            if (data.message == "OK") {
                setPromptsGenStatus("QUEUED")
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/simulation-prompts`);
            }

        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true
            });
        } finally {
            setLoadingSave(false);
        }
    };

    const runPromptSimulations = async () => {
        setLoadingSave(true);
        setErrorMessage({ message: "", display: false });

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/geo_setup`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id: account_id,
                    org_id: org_id,
                    geo_id: geo_id,
                    property_id: null,
                    language: null,
                    contexts: null,
                    goal: null,
                    topics: null,
                    engines: null,
                    simulation_count: null,
                    simulation_completed: true
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Something went wrong");
            }

            if (data.message == "OK") {
                setPromptSumulationsRun("QUEUED")
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/geo-overview/overview`);
            }

        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true
            });
        } finally {
            setLoadingSave(false);
        }
    };

    const exitGeo = () => {
        navigate(`/console/organizations/${org_id}/geo`)
    }

    useEffect(() => {
        if (account_id && org_id && geo_id) {
            fetchData();
            fetchGeoSettings()
        }
    }, [account_id, org_id, geo_id]);

    return (
        <div className="open_frame_cont">
            <div className="open_frame_cont_header">
                {loading ? (
                    <div className="open_frame_title_loading_cont">
                        <div className="open_frame_title_loading"></div>
                        <div className="open_frame_button_loading"></div>
                    </div>
                ) : (
                    <>
                        <div className="open_frame_title fade-in">
                            <button className="open_frame_cont_header_btn_exit" onClick={exitGeo}><i className="fa-solid fa-chevron-left"></i></button>
                            {data?.name || geo_id}
                        </div>
                        <button className="open_frame_cont_header_btn fade-in" onClick={() => setOpenSettingsModal(true)}>
                            <HiOutlineDotsVertical />
                        </button>
                    </>
                )}
            </div>

            {loading ? (
                <MainGeoLoader />
            ) : (
                <div className="open_frame_body">
                    {geo_page == "property" ? (
                        <GeoProperty
                            account_id={account_id}
                            org_id={org_id}
                            geo_id={geo_id}
                            selectedPropertyId={selectedPropertyId}
                            setSelectedPropertyId={setSelectedPropertyId}
                            savePropertyGeo={savePropertyGeo}
                            loadingSave={loadingSave}
                            setErrorMessage={setErrorMessage}
                            errorMessage={errorMessage}
                        />
                    ) : geo_page == "language" ? (
                        <GeoLanguage
                            account_id={account_id}
                            org_id={org_id}
                            geo_id={geo_id}
                            selectedLanguage={selectedLanguage}
                            setSelectedLanguage={setSelectedLanguage}
                            saveLanguageGeo={saveLanguageGeo}
                            setLoadingSave={setLoadingSave}
                            loadingSave={loadingSave}
                            setErrorMessage={setErrorMessage}
                            errorMessage={errorMessage}
                        />
                    ) : geo_page == "context" ? (
                        <GeoContext
                            account_id={account_id}
                            org_id={org_id}
                            geo_id={geo_id}
                            selectedContexts={selectedContexts}
                            setSelectedContexts={setSelectedContexts}
                            saveContextGeo={saveContextGeo}
                            loadingSave={loadingSave}
                            setLoadingSave={setLoadingSave}
                            errorMessage={errorMessage}
                            setErrorMessage={setErrorMessage}
                        />
                    ) : geo_page == "goal" ? (
                        <GeoGoal
                            account_id={account_id}
                            org_id={org_id}
                            geo_id={geo_id}
                            goalDesc={goalDesc}
                            setGoalDesc={setGoalDesc}
                            saveGoalGeo={saveGoalGeo}
                            loadingSave={loadingSave}
                            setLoadingSave={setLoadingSave}
                            errorMessage={errorMessage}
                            setErrorMessage={setErrorMessage}
                        />
                    ) : geo_page == "topics" ? (
                        <GeoTopics
                            account_id={account_id}
                            org_id={org_id}
                            geo_id={geo_id}
                            selectedContexts={selectedContexts}
                            selectedTopics={selectedTopics}
                            setSelectedTopics={setSelectedTopics}
                            saveTopicsGeo={saveTopicsGeo}
                            setLoadingSave={setLoadingSave}
                            loadingSave={loadingSave}
                            errorMessage={errorMessage}
                            setErrorMessage={setErrorMessage}
                        />
                    ) : geo_page == "engines" ? (
                        <GeoEngines
                            account_id={account_id}
                            org_id={org_id}
                            geo_id={geo_id}
                            selectedEngines={selectedEngines}
                            setSelectedEngines={setSelectedEngines}
                            count={count}
                            setCount={setCount}
                            saveEnginesGeo={saveEnginesGeo}
                            setLoadingSave={setLoadingSave}
                            loadingSave={loadingSave}
                            errorMessage={errorMessage}
                            setErrorMessage={setErrorMessage}
                        />
                    ) : geo_page == "simulation-prompts" ? (
                        <GeoPrompts
                            account_id={account_id}
                            org_id={org_id}
                            geo_id={geo_id}
                            promptsGenStatus={promptsGenStatus}
                            setPromptsGenStatus={setPromptsGenStatus}
                            runPromptSimulations={runPromptSimulations}
                            setLoadingSave={setLoadingSave}
                            loadingSave={loadingSave}
                            errorMessage={errorMessage}
                            setErrorMessage={setErrorMessage}
                        />
                    ) : geo_page == "geo-overview" && (
                        <GeoOverview
                            account_id={account_id}
                            org_id={org_id}
                            geo_id={geo_id}
                            fetchGeoSettings={fetchGeoSettings}
                            geoSettings={geoSettings}
                        />
                    )}
                </div>
            )}

            <SettingsGeoModal
                account_id={account_id}
                show={openSettingsModal}
                handleClose={() => setOpenSettingsModal(false)}
                fetchRecords={fetchData}
                setGeoData={setGeoData}
                geoData={geoData}
                setOpenDeleteGeo={setOpenDeleteGeo}
            />

            <DeleteItemModal
                account_id={account_id}
                show={openDeleteGeo}
                handleClose={() => setOpenDeleteGeo(false)}
                fetchRecords={exitGeo}
                item={geoData}
                endpoint={"delete_geo"}
                title={t.geo.delete_title}
                description={t.geo.delete_desc}
                input_value={geoData}
            />
        </div>
    )
}

export default OpenGeo